/**
 * Product Kit Builder
 * Gerencia a interface de montagem de kits personalizáveis
 */

class ProductKitBuilder {
  constructor(container, config) {
    this.container = container;
    this.config = config;
    this.currentSlide = 0;
    this.selections = new Map(); // key: `${productId}-${rep}` -> { productId, variantId }
    this.isAccordion = (config.kitLayout || container.dataset.kitLayout || 'wizard') === 'accordion';
    this.kitComplete = false;
    /** @type {Record<string, Array<{src: string, alt: string}>>} */
    this._productMediaCache = {};
    /** @type {number} */
    this._galleryUpdateToken = 0;

    // Elementos DOM
    this.slider = container.querySelector('[data-kit-slider]');
    this.slides = Array.from(this.slider.querySelectorAll('.kit-product-slide'));
    this.prevButton = container.querySelector('[data-kit-prev]');
    this.nextButton = container.querySelector('[data-kit-next]');
    this.addButton = container.querySelector('[data-kit-add]');
    this.currentCounter = container.querySelector('[data-kit-current]');
    this.totalCounter = container.querySelector('[data-kit-total]');
    
    this.init();
  }

  init() {
    // Inicializar seleções para todas as instâncias de slides
    const allSlides = Array.from(this.container.querySelectorAll('.kit-product-slide'));
    allSlides.forEach((slide) => {
      const productId = parseInt(slide.dataset.productId);
      const rep = parseInt(slide.dataset.rep || '1');
      const productData = this.config.products.find((p) => p.id === productId);
      const key = `${productId}-${rep}`;
      if (productData) {
        this.selections.set(key, {
          productId,
          variantId: productData.firstAvailableVariantId
        });
      }
    });

    // Aplicar tamanho de kit padrão (primeiro valor do CSV)
    const defaultSize =
      this.config.kitDefaultSize ||
      (this.config.kitSizes && this.config.kitSizes.length > 0
        ? parseInt(this.config.kitSizes[0], 10)
        : 2);
    this.applyKitSize(defaultSize);

    this.setupEventListeners();
    this.showSlide(0);
    this.updateKitSummary();
  }

  setupEventListeners() {
    this.prevButton?.addEventListener('click', () => this.previousSlide());
    this.nextButton?.addEventListener('click', () => this.nextSlide());
    this.addButton?.addEventListener('click', () => this.addToCart());

    // Radio buttons de variantes (delegação)
    this.container.addEventListener('change', (e) => {
      const radio = e.target.closest('.kit-variant-radio');
      if (radio) this.handleVariantChange(e);
    });

    this.container.addEventListener('keydown', (e) => this.handleKeyboard(e));

    this.container.addEventListener('click', (e) => {
      const nextInner = e.target.closest('[data-kit-next-inner]');
      if (nextInner) {
        e.preventDefault();
        e.stopPropagation();
        this.nextSlide();
        return;
      }

      const trigger = e.target.closest('[data-kit-accordion-trigger]');
      if (!trigger || !this.isAccordion) return;
      const slide = trigger.closest('.kit-product-slide');
      if (!slide || slide.getAttribute('data-hidden') === 'true') return;
      const index = this.slides.indexOf(slide);
      if (index < 0) return;
      if (slide.classList.contains('is-open')) {
        this.closeAllAccordions();
      } else {
        this.showSlide(index);
      }
    });

    // Seletor de tamanhos do kit
    const sizeButtons = this.container.querySelectorAll('[data-kit-size]');
    sizeButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const size = parseInt(btn.dataset.kitSize);
        this.applyKitSize(size);

        // Atualiza estados visuais dos botões
        sizeButtons.forEach((b) => {
          const pressed = parseInt(b.dataset.kitSize) === size;
          b.setAttribute('aria-pressed', pressed ? 'true' : 'false');
          b.classList.toggle('is-active', pressed);
        });

        // Subtextos permanecem sempre visíveis por opção
      });
    });
  }

  getVisibleSlides() {
    return Array.from(this.slider.querySelectorAll('.kit-product-slide:not([data-hidden="true"])'));
  }

  applyKitSize(size) {
    // Oculta/mostra slides por repetição
    const allSlides = Array.from(this.slider.querySelectorAll('.kit-product-slide'));
    allSlides.forEach((slide) => {
      const rep = parseInt(slide.dataset.rep || '1');
      if (rep > size) {
        slide.style.display = 'none';
        slide.setAttribute('data-hidden', 'true');
      } else {
        slide.style.display = '';
        slide.removeAttribute('data-hidden');
      }
    });

    this.slides = this.getVisibleSlides();
    if (this.totalCounter) {
      this.totalCounter.textContent = this.slides.length;
    }

    const previousIndex = this.currentSlide;
    const maxIndex = Math.max(0, this.slides.length - 1);
    let nextIndex = 0;

    if (this.isAccordion) {
      this.kitComplete = false;
      if (previousIndex < 0) {
        nextIndex = -1;
      } else {
        nextIndex = Math.min(previousIndex, maxIndex);
      }
    } else {
      nextIndex = Math.min(Math.max(previousIndex, 0), maxIndex);
    }

    if (nextIndex < 0) {
      this.closeAllAccordions();
    } else {
      this.showSlide(nextIndex);
    }

    // Atualiza desconto por tamanho conforme modo configurado (percent | fixed)
    const configuredMode = this.config.discountMode || 'percent';
    const discountPercent = (this.config.kitDiscounts && this.config.kitDiscounts[String(size)]) || 0;
    const discountFixed = (this.config.kitDiscountsFixed && this.config.kitDiscountsFixed[String(size)]) || 0;

    if (configuredMode === 'fixed' && discountFixed > 0) {
      this.config.discountMultiplier = 1.0;
      this.config.discountFixedCents = discountFixed;
      this.config.discountType = 'fixed';
      this.config.activeDiscountPercent = 0;
    } else if (configuredMode === 'percent' && discountPercent > 0) {
      this.config.discountMultiplier = (100 - discountPercent) / 100;
      this.config.discountFixedCents = 0;
      this.config.discountType = 'percent';
      this.config.activeDiscountPercent = discountPercent;
    } else {
      this.config.discountMultiplier = 1;
      this.config.discountFixedCents = 0;
      this.config.discountType = 'none';
      this.config.activeDiscountPercent = 0;
    }

    // Recalcular preços dos slides visíveis
    const visibleSlides = this.getVisibleSlides();
    this.slides.forEach((slide) => {
      const productId = parseInt(slide.dataset.productId);
      const rep = parseInt(slide.dataset.rep || '1');
      const key = `${productId}-${rep}`;
      const selection = this.selections.get(key);
      const productData = this.config.products.find((p) => p.id === productId);
      let variant = undefined;
      if (selection) {
        variant = productData?.variants.find((v) => v.id === selection.variantId) || productData?.variants[0];
      } else {
        variant = productData?.variants[0];
      }
      if (variant) {
        this.updateProductPrice(slide, variant, visibleSlides.length);
      }
      this.updateAccordionSummary(slide);
    });
    this.updateKitSummary();
  }

  handleVariantChange(event) {
    const radio = event.target;
    if (!radio.checked) return;

    const slide = radio.closest('.kit-product-slide');
    const productIndex = parseInt(slide.dataset.productIndex) - 1;
    const productId = parseInt(slide.dataset.productId);
    const rep = parseInt(slide.dataset.rep || '1');
    const fieldset = radio.closest('.kit-variant-fieldset');
    
    // Atualizar valor atual exibido
    const currentValueSpan = fieldset.querySelector('.current-value');
    if (currentValueSpan) {
      currentValueSpan.textContent = radio.value;
      currentValueSpan.dataset.currentValue = radio.value;
    }

    // Obter todas as opções selecionadas
    const selectedOptions = Array.from(
      slide.querySelectorAll('.current-value')
    ).map((el) => el.dataset.currentValue);

    console.log('🔍 Opções selecionadas:', selectedOptions);

    // Encontrar produto e variante correspondente
    const product = this.config.products[productIndex];
    const variant = product.variants.find((v) => {
      return JSON.stringify(v.options) === JSON.stringify(selectedOptions);
    });

    if (variant) {
      console.log('✅ Variante encontrada:', variant);
      
      // Atualizar seleção específica desta instância
      const key = `${productId}-${rep}`;
      this.selections.set(key, {
        productId,
        variantId: variant.id
      });

      // Atualizar imagem
      this.updateProductImage(slide, variant);

      // Atualizar preço
      const visibleSlides = this.getVisibleSlides();
      this.updateProductPrice(slide, variant, visibleSlides.length);

      // Atualizar disponibilidade dos botões
      this.updateNavigationAvailability(slide, variant);

      this.updateSwatchAvailability(slide, product, selectedOptions);
      this.updateAccordionSummary(slide);
      this.updateKitSummary();
    } else {
      console.error('❌ Variante não encontrada para:', selectedOptions);
    }
  }

  updateProductImage(slide, variant) {
    const image = slide.querySelector('[data-kit-image]');

    if (image && variant.featured_image) {
      image.src = variant.featured_image;
      image.alt = variant.options.join(' - ');
    }

    const slideProductId = parseInt(slide.dataset.productId, 10);
    const productData = this.config.products.find((p) => p.id === slideProductId);
    if (!productData) return;

    // Preferir opção cujo fieldset é de cor; fallback options[0]
    let colorValue = '';
    const fieldsets = slide.querySelectorAll('.kit-variant-fieldset');
    fieldsets.forEach((fs) => {
      const legend = fs.querySelector('.kit-variant-legend');
      const name = (legend?.childNodes?.[0]?.textContent || legend?.textContent || '').toLowerCase();
      if (name.includes('cor') || name.includes('color')) {
        colorValue = fs.querySelector('.current-value')?.dataset?.currentValue || '';
      }
    });
    if (!colorValue) {
      colorValue = variant.options[0] || '';
    }

    this.updateGalleryWithProductImages(productData.handle, colorValue);
  }

  updateProductPrice(slide, variant, totalProducts = 1) {
    const priceElement = slide.querySelector('[data-kit-price]');
    if (!priceElement) return;

    let discountedPrice = variant.price;
    let hasDiscount = false;

    // Aplicar desconto conforme tipo
    if (this.config.discountType === 'fixed' && this.config.discountFixedCents > 0) {
      // Desconto fixo: dividir proporcionalmente por produto
      const discountPerProduct = Math.floor(this.config.discountFixedCents / totalProducts);
      discountedPrice = Math.max(0, variant.price - discountPerProduct);
      hasDiscount = discountPerProduct > 0;
    } else if (this.config.discountType === 'percent' && this.config.discountMultiplier < 1) {
      // Desconto por porcentagem
      discountedPrice = variant.price * this.config.discountMultiplier;
      hasDiscount = true;
    } else if (this.config.discountType === 'fixed' && this.config.discountFixedCents > 0) {
      // Desconto fixo geral
      const discountPerProduct = Math.floor(this.config.discountFixedCents / totalProducts);
      discountedPrice = Math.max(0, variant.price - discountPerProduct);
      hasDiscount = discountPerProduct > 0;
    }

    priceElement.textContent = this.formatMoney(discountedPrice);

    // Mostrar preço cortado se houver desconto
    const compareElement = slide.querySelector('[data-kit-compare-price]');
    if (compareElement) {
      if (hasDiscount) {
        compareElement.textContent = this.formatMoney(variant.price);
        compareElement.style.display = '';
      } else {
        compareElement.style.display = 'none';
      }
    }
  }

  updateNavigationAvailability(slide, variant) {
    if (!variant.available) {
      if (this.nextButton) this.nextButton.disabled = true;
      if (this.addButton) {
        if (this.isAccordion && !this.kitComplete) {
          this.addButton.disabled = false;
        } else {
          this.addButton.disabled = true;
        }
      }
    } else {
      if (this.nextButton) this.nextButton.disabled = false;
      if (this.addButton) this.addButton.disabled = false;
    }
  }

  updateSwatchAvailability(slide, product, selectedOptions) {
    const fieldsets = slide.querySelectorAll('.kit-variant-fieldset');
    
    fieldsets.forEach((fieldset, index) => {
      // Pular a primeira opção (geralmente cor)
      if (index === 0 && fieldsets.length > 1) return;

      const radioButtons = fieldset.querySelectorAll('input[type="radio"]');
      const previousOption = index > 0 ? selectedOptions[index - 1] : null;

      // Filtrar variantes relevantes
      const relevantVariants = previousOption
        ? product.variants.filter((v) => v.options[index - 1] === previousOption)
        : product.variants;

      radioButtons.forEach((radio) => {
        const label = radio.nextElementSibling;
        const value = radio.value;
        
        // Verificar se esta opção está disponível
        const isAvailable = relevantVariants.some(
          (v) => v.options[index] === value && v.available
        );

        if (isAvailable) {
          radio.disabled = false;
          label.classList.add('available');
          label.classList.remove('soldout');
        } else {
          radio.disabled = true;
          label.classList.add('soldout');
          label.classList.remove('available');
        }
      });
    });
  }

  showSlide(index) {
    if (!this.slides.length) return;

    if (this.isAccordion) {
      this.slides.forEach((slide, i) => {
        slide.style.display = '';
        const open = i === index;
        slide.classList.toggle('is-open', open);
        const trigger = slide.querySelector('[data-kit-accordion-trigger]');
        if (trigger) trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
      this.currentSlide = index;
      if (this.currentCounter) this.currentCounter.textContent = index + 1;
      if (this.slides[index]) {
        this.applyDefaultVariantSelection(this.slides[index]);
        this.updateGalleryForCurrentSlide(this.slides[index]);
      }
      this.updateNavigation();
      this.updateKitSummary();
      return;
    }

    this.slides.forEach((slide) => {
      slide.style.display = 'none';
      slide.classList.remove('is-open');
    });

    if (this.slides[index]) {
      this.slides[index].style.display = 'block';
      this.currentSlide = index;
      if (this.currentCounter) this.currentCounter.textContent = index + 1;
      this.updateNavigation();
      this.updateGalleryForCurrentSlide(this.slides[index]);
    }
  }

  closeAllAccordions() {
    if (!this.isAccordion) return;
    this.slides.forEach((slide) => {
      slide.classList.remove('is-open');
      const trigger = slide.querySelector('[data-kit-accordion-trigger]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    });
    this.currentSlide = -1;
    this.updateNavigation();
    this.updateKitSummary();
  }

  applyDefaultVariantSelection(slide) {
    if (!slide) return;
    const productId = parseInt(slide.dataset.productId, 10);
    const rep = parseInt(slide.dataset.rep || '1', 10);
    const productIndex = parseInt(slide.dataset.productIndex, 10) - 1;
    const product = this.config.products[productIndex] || this.config.products.find((p) => p.id === productId);
    if (!product) return;

    const key = `${productId}-${rep}`;
    const selection = this.selections.get(key);
    let variant =
      product.variants.find((v) => v.id === selection?.variantId) ||
      product.variants.find((v) => v.id === product.firstAvailableVariantId) ||
      product.variants.find((v) => v.available) ||
      product.variants[0];
    if (!variant) return;

    const fieldsets = slide.querySelectorAll('.kit-variant-fieldset');
    const selectedOptions = [];

    fieldsets.forEach((fieldset, optIndex) => {
      const desired = variant.options[optIndex];
      const radios = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
      let radio =
        radios.find((r) => r.value === desired && !r.disabled) ||
        radios.find((r) => !r.disabled) ||
        radios[0];
      if (!radio) return;
      radio.checked = true;
      const currentValueSpan = fieldset.querySelector('.current-value');
      if (currentValueSpan) {
        currentValueSpan.textContent = radio.value;
        currentValueSpan.dataset.currentValue = radio.value;
      }
      selectedOptions[optIndex] = radio.value;
    });

    const matched = product.variants.find(
      (v) => JSON.stringify(v.options) === JSON.stringify(selectedOptions)
    );
    if (matched) variant = matched;

    this.selections.set(key, { productId, variantId: variant.id });
    this.updateProductImage(slide, variant);
    this.updateProductPrice(slide, variant, this.getVisibleSlides().length);
    this.updateSwatchAvailability(slide, product, selectedOptions);
    this.updateAccordionSummary(slide);
    this.updateNavigationAvailability(slide, variant);
  }

  updateAccordionSummary(slide) {
    const summary = slide?.querySelector('[data-kit-accordion-summary]');
    if (!summary) return;
    const values = Array.from(slide.querySelectorAll('.current-value'))
      .map((el) => (el.textContent || '').trim())
      .filter(Boolean);
    summary.textContent = values.join(' / ');
  }

  updateKitSummary() {
    const totalEl = this.container.querySelector('[data-kit-total-price]');
    const savingsWrap = this.container.querySelector('[data-kit-savings]');
    const savingsAmt = this.container.querySelector('[data-kit-savings-amount]');
    if (!totalEl) return;

    let total = 0;
    let compare = 0;
    const visible = this.getVisibleSlides();

    visible.forEach((slide) => {
      const productId = parseInt(slide.dataset.productId, 10);
      const rep = parseInt(slide.dataset.rep || '1', 10);
      const productIndex = parseInt(slide.dataset.productIndex, 10) - 1;
      const product = this.config.products[productIndex] || this.config.products.find((p) => p.id === productId);
      const selection = this.selections.get(`${productId}-${rep}`);
      const variant =
        product?.variants.find((v) => v.id === selection?.variantId) ||
        product?.variants.find((v) => v.id === product.firstAvailableVariantId) ||
        product?.variants[0];
      if (!variant) return;

      compare += variant.price;
      if (this.config.discountType === 'percent' && this.config.discountMultiplier < 1) {
        total += variant.price * this.config.discountMultiplier;
      } else if (this.config.discountType === 'fixed' && this.config.discountFixedCents > 0) {
        const discountPerProduct = Math.floor(this.config.discountFixedCents / Math.max(visible.length, 1));
        total += Math.max(0, variant.price - discountPerProduct);
      } else {
        total += variant.price;
      }
    });

    totalEl.textContent = this.formatMoney(Math.round(total));
    const saved = Math.round(compare - total);
    if (savingsWrap && savingsAmt && saved > 0) {
      savingsAmt.textContent = this.formatMoney(saved);
      savingsWrap.style.display = '';
    } else if (savingsWrap) {
      savingsWrap.style.display = 'none';
    }
  }

  /**
   * Atualiza galeria para o slide atual
   * @param {HTMLElement} slide - Slide atual
   */
  updateGalleryForCurrentSlide(slide) {
    const productId = parseInt(slide.dataset.productId, 10);
    const productData = this.config.products.find((p) => p.id === productId);
    if (!productData) return;

    let selectedColor = '';
    slide.querySelectorAll('.kit-variant-fieldset').forEach((fs) => {
      const legend = fs.querySelector('.kit-variant-legend');
      const name = (legend?.childNodes?.[0]?.textContent || legend?.textContent || '').toLowerCase();
      if (name.includes('cor') || name.includes('color')) {
        selectedColor = fs.querySelector('.current-value')?.dataset?.currentValue || '';
      }
    });
    if (!selectedColor) {
      const values = Array.from(slide.querySelectorAll('.current-value')).map(
        (el) => el.dataset.currentValue
      );
      selectedColor = values[0] || '';
    }

    this.updateGalleryWithProductImages(productData.handle, selectedColor);
  }

  previousSlide() {
    if (this.isAccordion && this.currentSlide < 0) return;
    if (this.currentSlide > 0) this.showSlide(this.currentSlide - 1);
  }

  nextSlide() {
    if (this.isAccordion && this.currentSlide < 0) {
      this.showSlide(0);
      return;
    }
    if (this.currentSlide < this.slides.length - 1) {
      this.showSlide(this.currentSlide + 1);
      return;
    }
    if (this.isAccordion) {
      this.kitComplete = true;
      this.closeAllAccordions();
    }
  }

  updateNavigation() {
    if (this.isAccordion) {
      if (this.nextButton) this.nextButton.style.display = 'none';
      if (this.prevButton) this.prevButton.style.display = 'none';
      if (this.addButton) {
        const labelEl = this.addButton.querySelector('.kit-add-button__label');
        const readyLabel = this.addButton.dataset.kitAddLabel || 'Adicionar ao carrinho';
        const pendingLabel = this.addButton.dataset.kitPendingLabel || 'Selecione as peças';
        this.addButton.style.display = 'flex';
        this.addButton.classList.toggle('is-ready', this.kitComplete);
        this.addButton.classList.toggle('kit-add-button--pending', !this.kitComplete);
        this.addButton.setAttribute('aria-disabled', this.kitComplete ? 'false' : 'true');
        this.addButton.setAttribute('aria-label', this.kitComplete ? readyLabel : pendingLabel);
        if (labelEl) labelEl.textContent = this.kitComplete ? readyLabel : pendingLabel;
        this.addButton.disabled = false;
      }
      this.slides.forEach((slide) => {
        const innerNext = slide.querySelector('[data-kit-next-inner]');
        if (innerNext) innerNext.hidden = false;
      });
      return;
    }

    if (this.prevButton) this.prevButton.disabled = this.currentSlide === 0;
    const isLastSlide = this.currentSlide === this.slides.length - 1;
    if (isLastSlide) {
      if (this.nextButton) this.nextButton.style.display = 'none';
      if (this.addButton) this.addButton.style.display = 'flex';
    } else {
      if (this.nextButton) this.nextButton.style.display = 'block';
      if (this.addButton) this.addButton.style.display = 'none';
    }
  }

  handleKeyboard(event) {
    switch (event.key) {
      case 'ArrowLeft':
        if (this.currentSlide > 0) {
          event.preventDefault();
          this.previousSlide();
        }
        break;
      case 'ArrowRight':
        if (this.currentSlide < this.slides.length - 1) {
          event.preventDefault();
          this.nextSlide();
        }
        break;
    }
  }

  buildDiscountTiersProperty() {
    const productsCount = (this.config.products && this.config.products.length) || 1;
    const sizes = this.config.kitSizes || [];
    const discounts = this.config.kitDiscounts || {};
    const parts = [];

    sizes.forEach((sizeStr) => {
      const size = parseInt(sizeStr, 10);
      if (!size || size < 1) return;
      const pieces = productsCount * size;
      const pct = parseInt(discounts[String(size)], 10) || 0;
      if (pieces > 0 && pct > 0) {
        parts.push(`${pieces}:${pct}`);
      }
    });

    return parts.join(',');
  }

  async addToCart() {
    if (!this.addButton) return;
    if (this.isAccordion && !this.kitComplete) return;
    this.addButton.disabled = true;
    this.addButton.setAttribute('aria-busy', 'true');

    const visibleSlides = this.getVisibleSlides();
    const items = [];
    const kitId = `kit-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    let kitLine = 0;

    visibleSlides.forEach((slide) => {
      const productId = parseInt(slide.dataset.productId);
      const rep = parseInt(slide.dataset.rep || '1');
      const key = `${productId}-${rep}`;
      const selection = this.selections.get(key);
      if (selection && selection.variantId) {
        kitLine += 1;
        items.push({
          id: selection.variantId,
          quantity: 1,
          properties: {
            _kit_id: kitId,
            _kit_line: String(kitLine),
            _kit_label: this.config.kitProductTitle || this.config.kitHeading || 'Conjunto',
            _kit_closed: this.config.closedKit ? 'true' : 'false',
            _kit_discount_percent:
              this.config.activeDiscountPercent > 0
                ? String(this.config.activeDiscountPercent)
                : '',
            _kit_discount_tiers: this.buildDiscountTiersProperty(),
          },
        });
      }
    });

    if (!items.length) {
      this.addButton.disabled = false;
      this.addButton.removeAttribute('aria-busy');
      return;
    }

    const cartAddUrl =
      (typeof Theme !== 'undefined' && Theme.routes && Theme.routes.cart_add_url) ||
      `${window.Shopify?.routes?.root || '/'}cart/add.js`;

    try {
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ items }),
      });

      const responseJson = await response.json().catch(() => ({}));

      if (response.ok && !responseJson.status) {
        const cartType = window.Theme?.cart?.type || 'drawer';
        if (cartType === 'page') {
          window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
          return;
        }

        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: responseJson,
              sourceId: this.container.dataset.kitId || '',
              data: {
                source: 'product-kit-builder',
                itemCount: responseJson.item_count,
                items: responseJson.items,
              },
            },
          })
        );
      } else {
        const message = responseJson.description || responseJson.message || 'Erro ao adicionar ao carrinho.';
        alert(message);
      }
    } catch (error) {
      console.error('Kit Builder addToCart:', error);
      alert('Erro ao adicionar produtos ao carrinho. Por favor, tente novamente.');
    } finally {
      this.addButton.disabled = false;
      this.addButton.removeAttribute('aria-busy');
    }
  }

  /**
   * Sync galeria Horizon: se for o mesmo produto da PDP, usa filtro data-media-color
   * (mesmo sistema de filter_images_by_color). Se for outro produto do kit, busca
   * imagens via /products/{handle}.js e aplica nos slides sem destruir media-gallery.
   * Imagens são pré-carregadas (tamanho da viewport) e aplicadas todas de uma vez.
   */
  async updateGalleryWithProductImages(productHandle, colorValue) {
    const gallery = document.querySelector('media-gallery');
    if (!gallery || !productHandle) return;

    const pageHandle = this.config.pageProductHandle;
    const sameProduct = !pageHandle || pageHandle === productHandle;

    if (sameProduct) {
      this.restoreOriginalGalleryIfNeeded(gallery);
      this.filterMainGalleryByColor(colorValue);
      return;
    }

    // Liquid já renderizou a 1.ª peça/cor — não trocar de novo no load (evita flash do combo).
    if (this.galleryAlreadyShowsKitProduct(gallery, productHandle, colorValue)) {
      gallery.dataset.kitExternalProduct = 'true';
      this.getProductImages(productHandle);
      return;
    }

    const token = ++this._galleryUpdateToken;

    try {
      const images = await this.getProductImages(productHandle);
      if (token !== this._galleryUpdateToken) return;
      await this.applyExternalProductImages(gallery, images, colorValue, token);
      gallery.dataset.kitGalleryHandle = productHandle;
      if (colorValue) {
        gallery.dataset.kitGalleryColor = this.normalizeColorText(colorValue, {
          forFilename: gallery.dataset.filterColorSource === 'filename',
        });
      }
    } catch (error) {
      console.error('Kit Builder gallery sync:', error);
    }
  }

  /**
   * HTML inicial do kit já traz handle+cor certos (data-kit-gallery-*).
   * @param {HTMLElement} gallery
   * @param {string} productHandle
   * @param {string} colorValue
   */
  galleryAlreadyShowsKitProduct(gallery, productHandle, colorValue) {
    const initialHandle = gallery.dataset.kitGalleryHandle || '';
    if (!initialHandle || initialHandle !== productHandle) return false;

    const useFilename = gallery.dataset.filterColorSource === 'filename';
    const wanted = this.normalizeColorText(colorValue, { forFilename: useFilename });
    const current = this.normalizeColorText(gallery.dataset.kitGalleryColor || '', {
      forFilename: useFilename,
    });

    if (wanted && current && wanted !== current) return false;

    // Já sincronizado por um update anterior — precisa aplicar de verdade.
    if (gallery.dataset.kitGallerySynced === 'true') return false;

    gallery.dataset.kitGallerySynced = 'true';
    return true;
  }

  /**
   * Cache de mídias por handle — evita refetch a cada troca de cor.
   * @param {string} productHandle
   * @returns {Promise<Array<{src: string, alt: string}>>}
   */
  async getProductImages(productHandle) {
    if (this._productMediaCache[productHandle]) {
      return this._productMediaCache[productHandle];
    }

    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}products/${productHandle}.js`);
    if (!response.ok) return [];
    const productData = await response.json();
    const images = this.extractProductImages(productData);
    this._productMediaCache[productHandle] = images;
    return images;
  }

  /**
   * CDN Shopify com width — evita carregar 3840px no kit.
   * @param {string} src
   * @param {number} width
   */
  sizedShopifyUrl(src, width) {
    if (!src) return '';
    const absolute = src.startsWith('//') ? `https:${src}` : src;
    try {
      const url = new URL(absolute, window.location.href);
      url.searchParams.set('width', String(width));
      return url.href;
    } catch {
      return absolute;
    }
  }

  /**
   * @param {string} src
   * @param {number[]} widths
   */
  buildShopifySrcset(src, widths = [300, 400, 500, 600, 700]) {
    return widths.map((width) => `${this.sizedShopifyUrl(src, width)} ${width}w`).join(', ');
  }

  /**
   * Largura alvo com base no tamanho visível da galeria.
   * @param {HTMLElement} gallery
   */
  getGalleryDisplayWidth(gallery) {
    const mediaWidth = gallery.querySelector('.product-media, slideshow-slide, .carousel-extended__slide')?.clientWidth;
    const width = mediaWidth || gallery.clientWidth || window.innerWidth || 1200;
    return Math.min(700, Math.max(300, Math.ceil(width * (window.devicePixelRatio || 1))));
  }

  /**
   * Pré-carrega URLs e só resolve quando todas estão prontas (ou falharam).
   * @param {string[]} urls
   */
  preloadImageUrls(urls) {
    const unique = [...new Set(urls.filter(Boolean))];
    return Promise.all(
      unique.map(
        (url) =>
          new Promise((resolve) => {
            const img = new Image();
            const done = () => resolve(url);
            img.onload = done;
            img.onerror = done;
            img.src = url;
          })
      )
    );
  }

  cacheOriginalGallery(gallery) {
    if (!gallery.dataset.kitGalleryCached) {
      gallery.dataset.kitGalleryCached = 'true';
      this._originalGalleryHTML = gallery.innerHTML;
    }
  }

  restoreOriginalGalleryIfNeeded(gallery) {
    if (gallery.dataset.kitExternalProduct === 'true' && this._originalGalleryHTML) {
      gallery.innerHTML = this._originalGalleryHTML;
      gallery.dataset.kitExternalProduct = 'false';
      delete gallery.dataset.kitSlideshowMulti;
    }
  }

  /**
   * Normalização alinhada a media-gallery.js (#filterImagesByColor) do FIT LI
   */
  normalizeColorText(text, { forFilename = false } = {}) {
    let value = (text || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\.(jpe?g|png|webp|gif|avif)\b/gi, '');
    if (forFilename) {
      value = value.replace(/[/\\]+/g, ' ');
    }
    return value
      .replace(/[-_.]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  getFilenameFromSrc(src) {
    return (src || '').split('?')[0].split('/').pop() || '';
  }

  matchesColorNeedle(text, selectedColor, forFilename) {
    if (!selectedColor) return true;
    const needle = ` ${selectedColor} `;
    const hay = ` ${this.normalizeColorText(text, { forFilename })} `;
    return hay.includes(needle);
  }

  setMediaVisible(element, visible) {
    // Limpa inline do Liquid (display:none !important) e classes de hide por cor
    element.style.removeProperty('display');
    element.classList.toggle('slideshow-controls__thumbnail--color-hidden', !visible);
    element.classList.toggle('dialog-thumbnails-list__thumbnail--color-hidden', !visible);
    if (visible) {
      element.removeAttribute('hidden');
      if (element instanceof HTMLElement) element.hidden = false;
    } else {
      element.style.display = 'none';
      element.setAttribute('hidden', 'true');
      if (element instanceof HTMLElement) element.hidden = true;
    }
  }

  getGalleryMediaElements(gallery) {
    return gallery.querySelectorAll(
      'slideshow-slide[data-media-color], li[data-media-color], .carousel-extended__slide[data-media-color], slideshow-slide, .carousel-extended__slide, .product-media-container'
    );
  }

  extractProductImages(productData) {
    if (Array.isArray(productData?.media) && productData.media.length) {
      return productData.media
        .map((item) => ({
          src: item.src || item.preview_image?.src || '',
          alt: item.alt || '',
        }))
        .filter((item) => item.src);
    }

    if (!Array.isArray(productData?.images)) return [];

    return productData.images.map((img) =>
      typeof img === 'string' ? { src: img, alt: '' } : { src: img.src || '', alt: img.alt || '' }
    );
  }

  expandGalleryItems(items, needed) {
    if (!items.length || items.length >= needed) return items;

    const parent = items[0].parentElement;
    if (!parent) return items;

    const template = items[0];
    const expanded = [...items];

    while (expanded.length < needed) {
      const index = expanded.length;
      const clone = template.cloneNode(true);
      clone.removeAttribute('hidden');
      clone.removeAttribute('reveal');
      clone.hidden = false;
      clone.style.display = '';
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('aria-selected', 'false');

      if (clone.hasAttribute('slide-id')) {
        clone.setAttribute('slide-id', `kit-ext-${index}`);
      }

      for (const el of clone.querySelectorAll('[id]')) {
        el.id = `${el.id}-kit-${index}`;
      }

      const clickTargets = [clone, ...clone.querySelectorAll('*')];
      for (const target of clickTargets) {
        const attr = target.getAttribute?.('on:click');
        if (attr && attr.includes('/select/')) {
          target.setAttribute('on:click', attr.replace(/\/select\/\d+/, `/select/${index}`));
        }
      }

      parent.appendChild(clone);
      expanded.push(clone);
    }

    return expanded;
  }

  /**
   * @param {HTMLImageElement | null} imgEl
   * @param {string | {src: string, alt?: string}} imgData
   * @param {{ width?: number, isThumb?: boolean }} [options]
   */
  applyImageToElement(imgEl, imgData, options = {}) {
    if (!imgEl || !imgData) return;
    const src = typeof imgData === 'string' ? imgData : imgData.src;
    const alt = typeof imgData === 'string' ? '' : imgData.alt || '';
    if (!src) return;

    const width = options.isThumb
      ? Math.min(400, options.width || 240)
      : options.width || 1200;
    const sizedSrc = this.sizedShopifyUrl(src, width);
    const srcset = options.isThumb
      ? this.buildShopifySrcset(src, [120, 240, 400])
      : this.buildShopifySrcset(src);

    imgEl.alt = alt;
    imgEl.srcset = srcset;
    imgEl.src = sizedSrc;
    if (!imgEl.getAttribute('sizes') && !options.isThumb) {
      imgEl.sizes = '(min-width: 750px) 50vw, 100vw';
    }
    imgEl.loading = 'eager';
    imgEl.fetchPriority = options.isThumb ? 'low' : 'high';
  }

  enableKitSlideshowIfNeeded(gallery, visibleCount) {
    const slideshow = gallery.querySelector('slideshow-component');
    if (!slideshow) return;

    slideshow.classList.toggle('slideshow--single-media', visibleCount <= 1);
    gallery.classList.toggle('product-media-gallery__slideshow--single-media', visibleCount <= 1);

    if (visibleCount <= 1) return;
    if (gallery.dataset.kitSlideshowMulti === 'true') {
      if (typeof slideshow.select === 'function') {
        slideshow.select(0, undefined, { animate: false });
      }
      return;
    }

    const parent = slideshow.parentNode;
    const next = slideshow.nextSibling;
    slideshow.remove();
    parent.insertBefore(slideshow, next);
    gallery.dataset.kitSlideshowMulti = 'true';
  }

  /**
   * @param {HTMLElement} gallery
   * @param {Array<{src: string, alt: string}>} images
   * @param {string} colorValue
   * @param {number} [token]
   */
  async applyExternalProductImages(gallery, images, colorValue, token = this._galleryUpdateToken) {
    this.cacheOriginalGallery(gallery);
    gallery.dataset.kitExternalProduct = 'true';

    const useFilename = gallery.dataset.filterColorSource === 'filename';
    const selectedColor = colorValue
      ? this.normalizeColorText(colorValue, { forFilename: useFilename })
      : '';

    let imagesToShow = images;
    if (selectedColor) {
      const filtered = images.filter((img) => {
        const src = typeof img === 'string' ? img : img.src || '';
        const alt = typeof img === 'string' ? '' : img.alt || '';
        const text = useFilename ? this.getFilenameFromSrc(src) : alt || this.getFilenameFromSrc(src);
        return this.matchesColorNeedle(text, selectedColor, useFilename);
      });
      if (filtered.length) imagesToShow = filtered;
    }

    if (!imagesToShow.length) return;

    const displayWidth = this.getGalleryDisplayWidth(gallery);
    // Pré-carrega todas as larguras do srcset — o browser pode escolher
    // outra width que a do display (ex.: 505) e aí o cache falhava.
    const preloadWidths = [300, 400, 500, 600, 700];
    if (!preloadWidths.includes(displayWidth)) preloadWidths.push(displayWidth);
    const preloadUrls = imagesToShow.flatMap((img) => {
      const src = typeof img === 'string' ? img : img.src || '';
      return preloadWidths.map((width) => this.sizedShopifyUrl(src, width));
    });

    gallery.classList.add('media-gallery--kit-swapping');
    await this.preloadImageUrls(preloadUrls);
    if (token !== this._galleryUpdateToken) {
      gallery.classList.remove('media-gallery--kit-swapping');
      return;
    }

    let slides = Array.from(
      gallery.querySelectorAll('slideshow-slide, .carousel-extended__slide')
    );
    if (!slides.length) {
      gallery.classList.remove('media-gallery--kit-swapping');
      return;
    }

    slides = this.expandGalleryItems(slides, imagesToShow.length);

    for (const [index, slide] of slides.entries()) {
      const imgData = imagesToShow[index];
      const imgEl = slide.querySelector('img.product-media__image, img');
      if (!imgData || !imgEl) {
        this.setMediaVisible(slide, false);
        continue;
      }

      this.applyImageToElement(imgEl, imgData, { width: displayWidth });
      const src = typeof imgData === 'string' ? imgData : imgData.src;
      const alt = typeof imgData === 'string' ? '' : imgData.alt || '';
      const colorText = useFilename ? this.getFilenameFromSrc(src) : alt || this.getFilenameFromSrc(src);
      slide.setAttribute('data-media-color', colorText);
      this.setMediaVisible(slide, true);
    }

    let thumbs = Array.from(
      gallery.querySelectorAll('.slideshow-controls__thumbnails button, .slideshow-controls__thumbnail')
    );
    if (thumbs.length) {
      thumbs = this.expandGalleryItems(thumbs, imagesToShow.length);
    }

    for (const [index, thumb] of thumbs.entries()) {
      const imgData = imagesToShow[index];
      if (!imgData) {
        this.setMediaVisible(thumb, false);
        continue;
      }
      this.applyImageToElement(thumb.querySelector('img'), imgData, { isThumb: true });
      this.setMediaVisible(thumb, true);
    }

    let dots = Array.from(gallery.querySelectorAll('.slideshow-controls__dots > li'));
    if (dots.length) {
      dots = this.expandGalleryItems(dots, imagesToShow.length);
      for (const [index, dot] of dots.entries()) {
        this.setMediaVisible(dot, Boolean(imagesToShow[index]));
      }
    }

    const counter = gallery.querySelector('.slideshow-controls__counter .slash');
    if (counter?.nextSibling) {
      counter.nextSibling.textContent = String(imagesToShow.length);
    }

    this.enableKitSlideshowIfNeeded(gallery, imagesToShow.length);

    const visibleImgs = [...gallery.querySelectorAll('slideshow-slide:not([hidden]) img, .carousel-extended__slide:not([hidden]) img')];
    await Promise.all(
      visibleImgs.map((img) => {
        if (typeof img.decode === 'function') {
          return img.decode().catch(() => {});
        }
        if (img.complete) return Promise.resolve();
        return new Promise((resolve) => {
          img.onload = () => resolve();
          img.onerror = () => resolve();
        });
      })
    );

    if (token !== this._galleryUpdateToken) return;
    gallery.classList.remove('media-gallery--kit-swapping');
  }

  /**
   * Filtra galeria da PDP por cor usando data-media-color (mesmo contrato do filtro nativo).
   */
  filterMainGalleryByColor(colorValue) {
    const gallery = document.querySelector('media-gallery');
    if (!gallery) return;

    const filterByColor = gallery.dataset.filterByColor === 'true';
    if (!filterByColor && !colorValue) return;

    const useFilename = gallery.dataset.filterColorSource === 'filename';
    const selectedColor = colorValue
      ? this.normalizeColorText(colorValue, { forFilename: useFilename })
      : '';

    if (!selectedColor) return;

    const mediaElements = gallery.querySelectorAll(
      'slideshow-slide[data-media-color], li[data-media-color], .carousel-extended__slide[data-media-color]'
    );

    if (!mediaElements.length) {
      // Fallback: slides sem data-media-color — filtra por alt/filename da img
      const slides = gallery.querySelectorAll('slideshow-slide, .carousel-extended__slide');
      let matchCount = 0;
      slides.forEach((slide) => {
        const img = slide.querySelector('img');
        if (!img) return;
        const text = useFilename
          ? this.getFilenameFromSrc(img.getAttribute('src'))
          : img.getAttribute('alt') || this.getFilenameFromSrc(img.getAttribute('src'));
        const match = this.matchesColorNeedle(text, selectedColor, useFilename);
        this.setMediaVisible(slide, match);
        if (match) matchCount += 1;
      });
      if (matchCount === 0) {
        slides.forEach((slide) => this.setMediaVisible(slide, true));
      }
      return;
    }

    let matchCount = 0;
    mediaElements.forEach((el) => {
      const match = this.matchesColorNeedle(el.dataset.mediaColor || '', selectedColor, useFilename);
      this.setMediaVisible(el, match);
      if (match) matchCount += 1;
    });

    if (matchCount === 0) {
      mediaElements.forEach((el) => this.setMediaVisible(el, true));
    }

    // Thumbnails com data-media-color ou img
    const thumbs = gallery.querySelectorAll(
      '.slideshow-controls__thumbnails button, .slideshow-control, [data-thumbnail]'
    );
    thumbs.forEach((thumb) => {
      const colorAttr = thumb.dataset.mediaColor;
      if (colorAttr != null) {
        this.setMediaVisible(thumb, this.matchesColorNeedle(colorAttr, selectedColor, useFilename));
        return;
      }
      const thumbImg = thumb.querySelector('img');
      if (!thumbImg) return;
      const thumbAlt = thumbImg.getAttribute('alt') || '';
      let text = thumbAlt || this.getFilenameFromSrc(thumbImg.getAttribute('src'));
      if (useFilename) {
        text = this.getFilenameFromSrc(thumbImg.getAttribute('src'));
        // Vídeo/3D: Shopify não expõe o nome original; paridade com media-color-source.liquid
        if (thumb.querySelector('.slideshow-controls__thumbnail-badge')) text += ` ${thumbAlt}`;
      }
      this.setMediaVisible(thumb, this.matchesColorNeedle(text, selectedColor, useFilename));
    });
  }

  normalizeString(str) {
    if (!str) return '';
    return str
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  formatMoney(cents) {
    const format =
      this.config.moneyFormat ||
      window.Theme?.moneyFormat ||
      window.themeVariables?.settings?.moneyFormat;
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents, format || '{{amount}}');
    }
    return `R$ ${(cents / 100).toFixed(2).replace('.', ',')}`;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-kit-id]').forEach((container) => {
    const kitId = container.dataset.kitId;
    const config = window.ProductKitBuilder?.[kitId];
    if (config) new ProductKitBuilder(container, config);
  });
});

